import { Cross2Icon } from '@radix-ui/react-icons';
import * as Popover from '@radix-ui/react-popover';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useContactEdit } from '@/features/projects/project-details/hooks/use-contact-edit';

type EditContactDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  projectId: number;
  initialContact: string | null;
  field?: 'main_contact' | 'contractor_contact' | 'client_contact';
  label?: string;
  children: ReactNode;
};

export const EditContactDialog = ({
  isOpen,
  onClose,
  projectId,
  initialContact,
  field = 'main_contact',
  label,
  children,
}: EditContactDialogProps) => {
  const { t } = useTranslation('project', { keyPrefix: 'Shared' });
  const { t: commonT } = useTranslation('common', { keyPrefix: 'button' });

  const { contact, setContact, handleSave, handleClose } = useContactEdit({
    projectId,
    initialContact,
    field,
    isOpen,
    onClose,
  });

  return (
    <Popover.Root open={isOpen} onOpenChange={handleClose}>
      <Popover.Trigger asChild>{children}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={8}
          className="z-50 w-[376px]"
        >
          <div
            className="relative rounded-[16px] border-[0.5px] border-transparent backdrop-blur-md"
            style={{
              background:
                'linear-gradient(rgba(255,255,255,0.2), rgba(255,255,255,0.2)), linear-gradient(135.632deg, rgba(68, 0, 155, 0.2) 0%, rgba(35, 112, 202, 0.2) 100%)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
            }}
          >
            <div className="rounded-[16px] bg-transparent p-[8px]">
              {/* Title Container */}
              <div className="mb-[8px] flex items-center justify-between px-[8px] pb-0 pl-[8px] pr-[6px] pt-[2px]">
                <p className="text-[13px] font-normal leading-[18px] tracking-[-0.4px] text-[#fefefe]">
                  {label || t('main_contact')}
                </p>
                <button
                  onClick={handleClose}
                  className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
                >
                  <Cross2Icon className="size-4 text-white" />
                  <span className="sr-only">Close</span>
                </button>
              </div>

              {/* Divider */}
              <div className="mb-[8px] h-[1px] w-full bg-white/50" />

              {/* Content */}
              <div className="space-y-[16px] px-[8px] pb-[8px]">
                {/* Input Field */}
                <div className="flex w-full items-center gap-[8px] overflow-clip rounded-[8px] border-[0.5px] border-[rgba(255,255,255,0.32)] bg-[rgba(255,255,255,0.05)] p-[8px] shadow-[inset_0px_20px_50px_0px_rgba(255,255,255,0.01)]">
                  <input
                    type="text"
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                    placeholder={t('enter_contact')}
                    className="w-full bg-transparent text-[13px] font-normal leading-[18px] tracking-[-0.4px] text-[#d9d9d9] placeholder:text-[#d9d9d9] focus:outline-none"
                  />
                </div>

                {/* Buttons */}
                <div className="flex w-full gap-[12px]">
                  <button
                    type="button"
                    onClick={handleClose}
                    className="flex flex-1 items-center justify-center rounded-[8px] border-[0.5px] border-solid border-white px-[16px] py-[10px] text-[13px] font-[510] tracking-[-0.4px] text-white transition-colors hover:bg-white/10"
                  >
                    {commonT('cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    className="flex flex-1 items-center justify-center rounded-[8px] border-[0.5px] border-solid border-white px-[16px] py-[10px] text-[13px] font-[510] tracking-[-0.4px] text-black transition-opacity hover:opacity-90"
                    style={{
                      backgroundImage:
                        'linear-gradient(105.141deg, rgb(161, 255, 128) 0.16086%, rgb(113, 178, 90) 147.39%)',
                    }}
                  >
                    {commonT('save')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
